import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { ME_QUERY } from '../graphql/queries';
import { LOGIN_MUTATION, LOGOUT_MUTATION } from '../graphql/mutations';
import { purgePersistedCache } from '../lib/apollo';
import { clearOutbox } from '../lib/outbox';

interface User {
  id: string;
  email: string;
  displayName?: string;
  timeZone?: string;
  createdAt?: string;
  updatedAt?: string;
  spaces?: {
    id: string;
    name: string;
    slug: string;
  }[];
}

/** Hook for auth state and actions: current user, login, logout, loading. */
export function useAuth() {
  const client = useApolloClient();
  const navigate = useNavigate();

  const { data, loading } = useQuery<{ me: User | null }>(ME_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const [loginMutation, { loading: loginLoading }] = useMutation(LOGIN_MUTATION);
  const [logoutMutation] = useMutation(LOGOUT_MUTATION);

  const user = data?.me || null;
  const isAuthenticated = !!user;

  const login = async (email: string, password: string) => {
    const result = await loginMutation({
      variables: { email, password },
    });

    client.writeQuery({
      query: ME_QUERY,
      data: { me: result.data.login.user },
    });

    return result.data.login;
  };

  const logout = async () => {
    await logoutMutation();
    // No document text may outlive the session in browser storage: drop the
    // offline outbox and the persisted Apollo snapshot before the in-memory
    // reset (whose refetches re-persist only the signed-out state).
    clearOutbox();
    purgePersistedCache();
    await client.resetStore();
    navigate('/login');
  };

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    loginLoading,
  };
}
